import dayjs from "dayjs";
import { ReactNode, useRef } from "react";
import { Fine } from "../../types/entities";
import { Amount, FineAvatar, outstandingFor, PAID_VIA, sentence, StatusChip, useFineMember } from "./FineRow";
import { Icon } from "./Icon";
import { Sheet } from "./Sheet";

/**
 * The whole of one fine. The list's cards hold the reason to two lines so
 * they can all be the same size; this is where the rest of it lives, along
 * with the dates and — for the minister — what can be done about it.
 */
export function FineDetailSheet({
  fine,
  canEdit,
  onClose,
  onMarkPaid,
  onEdit,
}: {
  fine: Fine | null;
  canEdit: boolean;
  onClose: () => void;
  onMarkPaid: (fine: Fine) => void;
  onEdit: (fine: Fine) => void;
}) {
  // Held on to after `fine` clears, so the sheet still has something to show
  // while it animates away rather than collapsing to empty on the way down.
  const shown = useRef(fine);
  if (fine) shown.current = fine;
  const current = fine ?? shown.current;

  return (
    <Sheet open={!!fine} onClose={onClose}>
      {current && <Body fine={current} canEdit={canEdit} onClose={onClose} onMarkPaid={onMarkPaid} onEdit={onEdit} />}
    </Sheet>
  );
}

function Body({
  fine,
  canEdit,
  onClose,
  onMarkPaid,
  onEdit,
}: {
  fine: Fine;
  canEdit: boolean;
  onClose: () => void;
  onMarkPaid: (fine: Fine) => void;
  onEdit: (fine: Fine) => void;
}) {
  const member = useFineMember(fine);
  const paid = fine.status === "paid";

  return (
    <div>
      <div className="flex items-center gap-3.5">
        <FineAvatar member={member} size="w-12 h-12" />
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[10px] tracking-[1.4px] uppercase text-ink3">
            Bøde til
          </div>
          <div className="font-display text-[22px] leading-tight font-extrabold uppercase tracking-tight truncate">
            {member?.name ?? "—"}
          </div>
        </div>
        <Amount value={fine.amount_kr} tone={paid ? "ink" : "clay"} size="text-[30px]" />
      </div>

      <p className="mt-5 text-[16px] leading-[1.5] text-ink break-words">{sentence(fine.reason)}</p>

      <div className="mt-5 rounded-lg border border-line-soft divide-y divide-line-soft">
        <Detail label="Status">
          <StatusChip fine={fine} />
        </Detail>
        <Detail label="Udstedt">{longDate(fine.created_at)}</Detail>
        {paid ? (
          <>
            {fine.paid_at && <Detail label="Betalt">{longDate(fine.paid_at)}</Detail>}
            {fine.paid_via && (
              <Detail label="Betalt via">
                <span className="inline-flex items-center gap-1.5">
                  <Icon name={PAID_VIA[fine.paid_via].icon} size={12} className="text-ink3" />
                  {PAID_VIA[fine.paid_via].label}
                </span>
              </Detail>
            )}
          </>
        ) : (
          <Detail label="Udestående">{sentence(outstandingFor(fine.created_at))}</Detail>
        )}
      </div>

      <div className="mt-5 flex flex-col gap-2">
        {canEdit && !paid && (
          <button
            onClick={() => onMarkPaid(fine)}
            className="w-full py-3.5 px-4 rounded-lg bg-vote-yes text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-center gap-2"
          >
            <Icon name="check" size={14} /> Markér betalt
          </button>
        )}
        {canEdit ? (
          <button
            onClick={() => onEdit(fine)}
            className="w-full py-3.5 px-4 rounded-lg bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-center gap-2"
          >
            <Icon name="pen-nib" size={13} /> Rediger bøde
          </button>
        ) : (
          <button
            onClick={onClose}
            className="w-full py-3.5 px-4 rounded-lg bg-ink text-paper font-display text-sm font-bold tracking-wide uppercase"
          >
            Luk
          </button>
        )}
      </div>
    </div>
  );
}

function longDate(iso: string) {
  return sentence(dayjs(iso).format("dddd D. MMMM YYYY"));
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 min-h-[48px] py-2">
      <span className="font-mono text-[10px] tracking-[1.4px] uppercase text-ink3">{label}</span>
      <span className="text-[14px] text-ink text-right">{children}</span>
    </div>
  );
}
